import React, { useEffect, useState } from 'react'
import { Typography } from '@mui/material'
import Button from '@/src/components/button'
import OtpInput from './OtpInput'

export interface Props {
  onVerify: (code: string) => void
  onBack: () => void
  loading?: boolean
  title?: string
  subtitle?: string
  emailHint?: string
  // seconds the challenge is valid for; omit to hide the countdown entirely
  expiresIn?: number
  backText?: string
}

const formatCountdown = (seconds: number) => {
  const safe = Math.max(0, seconds)
  const mm = Math.floor(safe / 60)
  const ss = safe % 60
  return `${mm}:${String(ss).padStart(2, '0')}`
}

const TwoFactorForm = ({
  onVerify,
  onBack,
  loading = false,
  title = 'Two-factor authentication',
  subtitle = 'Enter the 6-digit code from your authenticator app.',
  emailHint,
  expiresIn,
  backText = 'Back to sign in'
}: Props) => {
  // The challenge is valid for `expiresIn`; stop 10s short so a code entered on
  // the last tick still reaches the backend in time.
  const [remaining, setRemaining] = useState(() => (expiresIn ? Math.max(0, expiresIn - 10) : 0))
  const [code, setCode] = useState('')

  useEffect(() => {
    if (!expiresIn) return
    const timer = setInterval(() => {
      setRemaining(prev => (prev <= 1 ? 0 : prev - 1))
    }, 1000)
    return () => clearInterval(timer)
  }, [expiresIn])

  const timedOut = !!expiresIn && remaining <= 0

  return (
    <div className="flex flex-col items-center max-w-[26.5rem]">
      <Typography variant="h5" className="text-center">
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" className="text-center pt-2">
        {subtitle}
      </Typography>
      {emailHint && (
        <Typography variant="subtitle2" className="text-center pt-1">
          {emailHint}
        </Typography>
      )}

      <div className="pt-8 w-full">
        <OtpInput
          disabled={timedOut || loading}
          onChange={setCode}
          onComplete={value => {
            if (!timedOut) onVerify(value)
          }}
        />
      </div>

      {!!expiresIn && (
        <Typography
          variant="body2"
          color={timedOut ? 'error.main' : 'text.secondary'}
          className="pt-4 text-center"
        >
          {timedOut
            ? 'Code entry timed out — sign in again'
            : `Code expires in ${formatCountdown(remaining)}`}
        </Typography>
      )}

      <Button
        divClass="mt-6 w-full"
        text="Verify"
        type="button"
        fullwidth={true}
        loading={loading}
        disabled={timedOut || code.length !== 6}
        onClick={() => onVerify(code)}
      />

      <Typography
        component="button"
        type="button"
        variant="body2"
        color="primary.main"
        className="cursor-pointer pt-6 font-semibold bg-transparent border-0"
        onClick={onBack}
      >
        {backText}
      </Typography>
    </div>
  )
}

export default TwoFactorForm
